import ClientSelect from "../../ContractWizard/components/ClientSelect";
import { CONTRACT_TYPES } from "../constants";
import { btn, fromDocTag, input, label, notInDocTag, required, t, tabular } from "../styles";

const ClientCard = ({ client }) => {
  if (!client) {
    return (
      <div style={{ display: "flex", alignItems: "center", minHeight: 42, padding: "6px 12px", border: "1px dashed #dcdce3", borderRadius: 10, fontSize: 12.5, color: "#6f6f7c" }}>
        Pick a client to see their ID and contact details here
      </div>
    );
  }
  const isCompany = client.buyer_type === "LEGAL";
  const name = isCompany ? client.legal_company_name || client.name : `${client.name || ""} ${client.surname || ""}`.trim();
  const initials = name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
  const tint = isCompany ? "#e8f1fb" : "#eceafd";
  const ink = isCompany ? "#1c5b8f" : t.accent;
  const idLine = isCompany ? client.legal_fiscal_code && `CUI ${client.legal_fiscal_code}` : client.social_security_number && `CNP ${client.social_security_number}`;
  const email = isCompany ? client.business_email || client.personal_email : client.personal_email || client.business_email;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 42, padding: "6px 12px", border: "1px solid #eeeef2", borderRadius: 10, background: t.surfaceSubtle, minWidth: 0 }}>
      <div style={{ width: 30, height: 30, borderRadius: 8, background: tint, color: ink, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flex: "none" }}>
        {initials || "?"}
      </div>
      <div style={{ minWidth: 0, flex: 1, display: "flex", flexWrap: "wrap", columnGap: 14, rowGap: 2, alignItems: "center" }}>
        <span style={{ fontSize: 10, fontWeight: 650, letterSpacing: 0.4, textTransform: "uppercase", padding: "2px 7px", borderRadius: 5, background: tint, color: ink }}>
          {isCompany ? "Company" : "Individual"}
        </span>
        {idLine && <span style={{ fontSize: 12.5, color: "#3c3c46", whiteSpace: "nowrap", ...tabular }}>{idLine}</span>}
        {client.mobile_phone_number && <span style={{ fontSize: 12.5, color: "#3c3c46", whiteSpace: "nowrap" }}>{client.mobile_phone_number}</span>}
        {email && <span style={{ fontSize: 12.5, color: t.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{email}</span>}
      </div>
    </div>
  );
};

/**
 * Step 2: PVC, date, currency, notary, client, contract type, description.
 * `fromDoc` says which fields still hold the value read from the document,
 * so they can be tagged "From document".
 */
const DetailsStep = ({
  d,
  setD,
  tried,
  fromDoc,
  notaryMissingInDoc,
  pvcStatus,
  client,
  rate,
  onNewClient,
  clientNotInSystem,
}) => {
  const border = (bad) => (bad && tried ? "#e3a09d" : "#dcdce3");
  const pvcBorder = pvcStatus === "unavailable" ? "#e3a09d" : border(!String(d.pvc).trim());

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(110px,1fr) minmax(140px,1fr) auto minmax(180px,1.5fr)", gap: "18px 20px" }}>
      <label style={{ display: "block" }}>
        <span style={label}>
          PVC number {required}
          {fromDoc.pvc && <span style={fromDocTag}>From document</span>}
        </span>
        <input
          value={d.pvc}
          onChange={(e) => setD("pvc", e.target.value.replace(/\D/g, ""))}
          placeholder="e.g. 684"
          inputMode="numeric"
          style={{ ...input(pvcBorder), ...tabular }}
        />
        {pvcStatus === "checking" && <span style={{ display: "block", fontSize: 11.5, color: "#6f6f7c", marginTop: 5 }}>Checking…</span>}
        {pvcStatus === "available" && <span style={{ display: "block", fontSize: 11.5, color: t.success, marginTop: 5 }}>Available</span>}
        {pvcStatus === "unavailable" && <span style={{ display: "block", fontSize: 11.5, color: t.dangerText, marginTop: 5 }}>Already used this year</span>}
      </label>

      <label style={{ display: "block" }}>
        <span style={label}>
          PVC date {required}
          {fromDoc.date && <span style={fromDocTag}>From document</span>}
        </span>
        <input type="date" value={d.date} onChange={(e) => setD("date", e.target.value)} style={{ ...input(border(!d.date)), padding: "9px 11px" }} />
      </label>

      <div>
        <span style={label}>Currency {required}</span>
        <div style={{ display: "inline-flex", padding: 3, borderRadius: 10, background: "#f2f2f6", gap: 3 }}>
          {["RON", "EUR"].map((cur) => {
            const on = d.currency === cur;
            return (
              <button
                key={cur}
                type="button"
                onClick={() => setD("currency", cur)}
                style={{
                  font: "inherit",
                  fontSize: 13,
                  fontWeight: 600,
                  padding: "7px 18px",
                  borderRadius: 8,
                  border: "none",
                  background: on ? "#fff" : "transparent",
                  color: on ? t.text : t.textSecondary,
                  boxShadow: on ? "0 1px 3px rgba(20,20,32,0.12)" : "none",
                  cursor: "pointer",
                }}
              >
                {cur}
              </button>
            );
          })}
        </div>
        {d.currency === "RON" && (
          <div style={{ fontSize: 11.5, color: "#6f6f7c", marginTop: 6, whiteSpace: "nowrap" }}>
            Prices stay in EUR{rate ? ` · 1 EUR = ${rate.rate.toFixed(4)} RON · BNR ${rate.date || ""}` : ""}
          </div>
        )}
      </div>

      <label style={{ display: "block" }}>
        <span style={label}>
          Notary
          {notaryMissingInDoc && !d.notary && <span style={notInDocTag}>Not in document</span>}
          {fromDoc.notary && <span style={fromDocTag}>From document</span>}
        </span>
        <input value={d.notary} onChange={(e) => setD("notary", e.target.value)} placeholder="e.g. BNP Popescu & Asociații" style={input()} />
      </label>

      <div style={{ gridColumn: "1 / 3" }}>
        <span style={label}>
          Client {required}
          {fromDoc.client && <span style={fromDocTag}>From document</span>}
          {clientNotInSystem && !d.client && <span style={notInDocTag}>In document, not in the system yet</span>}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <ClientSelect value={d.client} onChange={(id) => setD("client", id)} invalid={tried && !d.client} />
          </div>
          <button type="button" className="cw2-btn" onClick={onNewClient} style={{ ...btn, flex: "none", fontSize: 12.5, padding: "0 13px", whiteSpace: "nowrap" }}>
            {clientNotInSystem && !d.client ? "+ Create from document" : "+ New client"}
          </button>
        </div>
      </div>
      <div style={{ gridColumn: "3 / -1", alignSelf: "end", minWidth: 0 }}>
        <ClientCard client={d.client ? client : null} />
      </div>

      <div style={{ gridColumn: "1 / -1" }}>
        <span style={label}>
          Contract type {required}
          {fromDoc.type && <span style={fromDocTag}>From document</span>}
        </span>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {CONTRACT_TYPES.map((ct) => {
            const on = d.type === ct.value;
            return (
              <button
                key={ct.value}
                type="button"
                className="cw2-chip"
                onClick={() => setD("type", ct.value)}
                style={{
                  font: "inherit",
                  fontSize: 13,
                  fontWeight: on ? 650 : 500,
                  padding: "8px 14px",
                  borderRadius: 9,
                  border: `1px solid ${on ? t.accent : tried && !d.type ? "#e3a09d" : "#dcdce3"}`,
                  background: on ? "#eceafd" : "#fff",
                  color: on ? t.accentHover : t.text,
                  cursor: "pointer",
                }}
              >
                {ct.label}
              </button>
            );
          })}
        </div>
      </div>

      <label style={{ display: "block", gridColumn: "1 / -1" }}>
        <span style={{ ...label, display: "block" }}>Description</span>
        <textarea
          value={d.description}
          onChange={(e) => setD("description", e.target.value)}
          rows={3}
          placeholder="Anything the team should know about this contract"
          style={{ ...input(), resize: "vertical", lineHeight: 1.5 }}
        />
      </label>
    </div>
  );
};

export default DetailsStep;
